// @djsem/ui — UI models for canonical UI prompts 84–88.
//
// Pure, framework-free logic backing the Electron screens. Each model exposes the shape a
// screen renders + the deterministic decisions (state, warnings, gating) so the React
// components stay thin and every rule is unit-testable.

// ============================================================================
// Prompt 84 — Set Prep UX (Set Builder / Cues / Phrases / Transitions / Coach)
// ============================================================================

/** A track as seen by the Set Prep screens. */
export interface SetPrepTrack {
  id: string;
  title: string;
  bpm?: number;
  key?: string;
  energy?: number;
  durationSec?: number;
  cueCount?: number;
  phraseCount?: number;
}

/** One point on the set's energy curve chart. */
export interface EnergyCurvePoint { position: number; energy: number; }
/**
 * Build the energy-curve series for a track order.
 * @param tracks - Ordered set-prep tracks.
 * @returns One {@link EnergyCurvePoint} per track (defaulting missing energy to 0.5).
 */
export function buildEnergyCurve(tracks: SetPrepTrack[]): EnergyCurvePoint[] {
  return tracks.map((t, i) => ({ position: i, energy: t.energy ?? 0.5 }));
}

/** A suggested transition between two adjacent tracks, with risk and notes. */
export interface TransitionSuggestion {
  fromTrackId: string;
  toTrackId: string;
  bpmDelta?: number;
  keyCompatibility: "same" | "adjacent" | "clash" | "unknown";
  risk: "low" | "medium" | "high";
  notes: string[];
}
/** Camelot-style key adjacency: same number or ±1 with same letter, or same number opposite letter. */
function keyCompat(a?: string, b?: string): TransitionSuggestion["keyCompatibility"] {
  if (!a || !b) return "unknown";
  if (a === b) return "same";
  const p = /^(\d+)([AB])$/i, ma = p.exec(a), mb = p.exec(b);
  if (!ma || !mb) return "unknown";
  const na = +ma[1], nb = +mb[1], la = ma[2].toUpperCase(), lb = mb[2].toUpperCase();
  if (la === lb && Math.abs(na - nb) === 1) return "adjacent";
  if (na === nb && la !== lb) return "adjacent";
  return "clash";
}
/**
 * Suggest transitions for each adjacent track pair, flagging BPM jumps, key clashes, and missing cues.
 * @param tracks - Ordered set-prep tracks.
 * @returns One {@link TransitionSuggestion} per adjacent pair.
 */
export function suggestTransitions(tracks: SetPrepTrack[]): TransitionSuggestion[] {
  const out: TransitionSuggestion[] = [];
  for (let i = 0; i < tracks.length - 1; i++) {
    const a = tracks[i], b = tracks[i + 1];
    const bpmDelta = a.bpm !== undefined && b.bpm !== undefined ? Math.abs(a.bpm - b.bpm) : undefined;
    const kc = keyCompat(a.key, b.key);
    const notes: string[] = [];
    if (bpmDelta !== undefined && bpmDelta > 6) notes.push(`BPM jump of ${bpmDelta.toFixed(1)} — plan a filter/echo transition`);
    if (kc === "clash") notes.push("Key clash — consider a break or filter cut");
    if (a.cueCount !== undefined && a.cueCount === 0) notes.push("Outgoing track has no cues — set an outro cue before the mix");
    const risk: TransitionSuggestion["risk"] =
      (kc === "clash" || (bpmDelta ?? 0) > 6) ? "high" : (kc === "adjacent" || (bpmDelta ?? 0) > 3) ? "medium" : "low";
    out.push({ fromTrackId: a.id, toTrackId: b.id, bpmDelta, keyCompatibility: kc, risk, notes });
  }
  return out;
}

/** Readiness checklist for snapshotting a set. */
export interface SetPrepChecklist { minCuesMet: boolean; energyArcPresent: boolean; noHighRiskTransitions: boolean; readyToSnapshot: boolean; }
/**
 * Evaluate whether a set is ready to snapshot: enough cues per track, a real energy arc, and no high-risk transitions.
 * @param tracks - Ordered set-prep tracks.
 * @param minCuesPerTrack - Minimum cues required per track; defaults to 2.
 * @returns The computed {@link SetPrepChecklist}.
 */
export function evaluateSetPrep(tracks: SetPrepTrack[], minCuesPerTrack = 2): SetPrepChecklist {
  const minCuesMet = tracks.every((t) => (t.cueCount ?? 0) >= minCuesPerTrack);
  const energyArcPresent = tracks.length >= 2 && tracks.some((t) => (t.energy ?? 0) >= 0.75);
  const noHighRiskTransitions = suggestTransitions(tracks).every((s) => s.risk !== "high");
  return { minCuesMet, energyArcPresent, noHighRiskTransitions, readyToSnapshot: minCuesMet && energyArcPresent && noHighRiskTransitions };
}

// ============================================================================
// Prompt 85 — Creative Studio UX (Brand World / Visual / AI Artwork·Video / Assets)
// ============================================================================

/** Summary of a Brand World's identity (palette, motifs, typography) for Creative Studio screens. */
export interface BrandWorldSummary {
  id: string;
  label: string;
  paletteHex: string[];
  motifs: string[];
  typographyFamilies: string[];
  approvedForPublicUse: boolean;
}

/** A request to generate artwork or video via the AI provider registry. */
export interface AIGenerationRequest {
  engine: "artwork" | "video";
  prompt: string;
  brandWorldId?: string;
  requestedForPublicOutput: boolean;
}

/** Preflight an AI-generation request BEFORE calling a provider (prompt 85, aligns with 65/66). */
export interface AIGenerationPreflight {
  ok: boolean;
  blockers: string[];
  warnings: string[];
  rightsStateAtOutput: "ai_generated_uncleared";
}
/**
 * Preflight an AI-generation request BEFORE calling a provider (prompt 85, aligns with 65/66).
 * @param req - The requested generation.
 * @returns The {@link AIGenerationPreflight} — blockers must be empty before generating.
 */
export function preflightAIGeneration(req: AIGenerationRequest): AIGenerationPreflight {
  const blockers: string[] = [];
  const warnings: string[] = [];
  if (!req.prompt || req.prompt.trim().length < 4) blockers.push("Prompt is empty or too short.");
  if (/copyrighted|logo of|movie still|album cover of/i.test(req.prompt)) blockers.push("Prompt references copyrighted material — refine before generating (rights policy §65).");
  if (req.requestedForPublicOutput) warnings.push("Output will be marked ai_generated_uncleared and must be cleared in the Rights Ledger before any public use (§65).");
  return { ok: blockers.length === 0, blockers, warnings, rightsStateAtOutput: "ai_generated_uncleared" };
}

/** A generated or imported asset (image/video/audio/document) with its rights state and provenance. */
export interface AssetSummary {
  id: string;
  kind: "image" | "video" | "audio" | "document";
  label: string;
  rightsState: "unknown" | "owned" | "licensed" | "restricted" | "ai_generated_uncleared";
  aiProvenance?: { providerId: string; modelId: string };
  sourcePlatform?: string;
}
/**
 * Group assets by rights state so the Asset Manager can show clear buckets.
 * @param assets - Assets to group.
 * @returns A record from rights state to the assets in that state.
 */
export function assetsByRightsState(assets: AssetSummary[]): Record<AssetSummary["rightsState"], AssetSummary[]> {
  const out = { unknown: [], owned: [], licensed: [], restricted: [], ai_generated_uncleared: [] } as Record<AssetSummary["rightsState"], AssetSummary[]>;
  for (const a of assets) out[a.rightsState].push(a);
  return out;
}

// ============================================================================
// Prompt 86 — Gig Workflow UX (Brief / Gig Pack / Client / Rider / Fan QR / CRM / Recap)
// ============================================================================

/** A single gig-pack checklist item. */
export interface GigChecklistItem { id: string; label: string; done: boolean; required: boolean; }
/** Full status of a gig's preparation pipeline (brief → pack → client → rider → fan page → recap). */
export interface GigPackStatus {
  briefApproved: boolean;
  setDrafted: boolean;
  packChecklist: GigChecklistItem[];
  clientPreviewSent: boolean;
  clientApproved: boolean;
  riderExported: boolean;
  fanPagePublished: boolean;
  postShowRecapDone: boolean;
}
/** Computed gig readiness: percent complete, outstanding blockers, and the next recommended action. */
export interface GigReadiness { readyPercent: number; blockers: string[]; nextAction: string; }
/**
 * Evaluate how ready a gig is, and what to do next.
 * @param status - Current gig pack status.
 * @returns The computed {@link GigReadiness}.
 */
export function evaluateGigReadiness(status: GigPackStatus): GigReadiness {
  const totalRequired = status.packChecklist.filter((i) => i.required).length + 6;
  const doneRequired = status.packChecklist.filter((i) => i.required && i.done).length
    + (status.briefApproved ? 1 : 0) + (status.setDrafted ? 1 : 0)
    + (status.clientPreviewSent ? 1 : 0) + (status.clientApproved ? 1 : 0)
    + (status.riderExported ? 1 : 0) + (status.fanPagePublished ? 1 : 0);
  const readyPercent = totalRequired === 0 ? 100 : Math.round((doneRequired / totalRequired) * 100);
  const blockers: string[] = [];
  if (!status.briefApproved) blockers.push("Event brief not approved");
  if (!status.setDrafted) blockers.push("Set not drafted");
  if (!status.clientApproved) blockers.push("Client approval outstanding");
  if (!status.riderExported) blockers.push("Technical rider not exported");
  const nextAction =
    !status.briefApproved ? "Approve event brief" :
    !status.setDrafted ? "Draft the set" :
    !status.clientPreviewSent ? "Send the client preview" :
    !status.clientApproved ? "Wait for client approval" :
    !status.riderExported ? "Export technical rider" :
    !status.fanPagePublished ? "Publish Fan QR page" :
    status.postShowRecapDone ? "All done" : "Prepare post-show recap";
  return { readyPercent, blockers, nextAction };
}

// ============================================================================
// Prompt 87 — Rehearsal / Live Conduct / Output / Adapter / Observability UX
// ============================================================================

/** Live/rehearsal safety mode — hardware output is never enabled regardless of mode (56/87). */
export type LiveSafetyMode = "normal" | "safe" | "blackout";
/** Raw telemetry from the live-bridge/event-bus, used to derive the Live dashboard state. */
export interface LiveOutputTelemetry {
  bridgeHealthy: boolean;
  lastHeartbeatAgeMs: number | null;
  jobBacklog: number;
  cachedApprovedPackages: number;
  eventBusRatePerSec: number;
  droppedEvents: number;
}

/** Derived Live dashboard state — hardware output is always locked to `false`. */
export interface LiveDashboardState {
  safety: LiveSafetyMode;
  banner: string;
  hardwareOutputActive: false;
  warnings: string[];
}

/**
 * Derive the Live dashboard state. Hardware output is ALWAYS locked to false (56/87).
 * @param t - Current live-output telemetry.
 * @returns The derived {@link LiveDashboardState}.
 */
export function deriveLiveDashboard(t: LiveOutputTelemetry): LiveDashboardState {
  const warnings: string[] = [];
  let safety: LiveSafetyMode = "normal";
  if (!t.bridgeHealthy) { safety = "safe"; warnings.push("Bridge is unhealthy — running in SAFE mode (simulator/output preview only)."); }
  if (t.lastHeartbeatAgeMs !== null && t.lastHeartbeatAgeMs > 5000) { safety = "safe"; warnings.push(`No heartbeat for ${(t.lastHeartbeatAgeMs / 1000).toFixed(1)}s.`); }
  if (t.droppedEvents > 0) warnings.push(`${t.droppedEvents} dropped event${t.droppedEvents === 1 ? "" : "s"} — check event-bus health.`);
  const banner =
    (safety as LiveSafetyMode) === "blackout" ? "BLACKOUT — all output stopped"
    : safety === "safe" ? "SAFE MODE — hardware output disabled, simulator active"
    : "Rehearsal/Live simulator ready — hardware output remains locked in this build";
  return { safety, banner, hardwareOutputActive: false, warnings };
}

/** A single adapter dry-run item: what it would do, never what it did. */
export interface AdapterDryRunItem { adapterId: string; destination: string; would: "create" | "update" | "skip"; note?: string; }
/**
 * Adapter Dry-Run must NEVER return `send` — real hardware/prod dispatch is gated by 56/87.
 * @param items - Dry-run items to summarize.
 * @returns Counts by outcome; `wouldSend` is always 0.
 */
export function summariseAdapterDryRun(items: AdapterDryRunItem[]): { total: number; wouldSend: 0; wouldCreate: number; wouldUpdate: number; wouldSkip: number } {
  return {
    total: items.length,
    wouldSend: 0,
    wouldCreate: items.filter((i) => i.would === "create").length,
    wouldUpdate: items.filter((i) => i.would === "update").length,
    wouldSkip: items.filter((i) => i.would === "skip").length,
  };
}

// ============================================================================
// Prompt 88 — Jobs / Notifications / Sync / Storage / Rights / AI / Billing / Settings / Admin
// ============================================================================

/** Lifecycle status of a background job, as shown in the Jobs screen. */
export type JobStatusUI = "queued" | "running" | "succeeded" | "failed" | "cancelled";
/** A job row as rendered in the Jobs list. */
export interface JobRowUI { id: string; label: string; status: JobStatusUI; progress?: number; attempts?: number; error?: string; }
/**
 * Which actions are available for a job's current status.
 * @param job - The job row to evaluate.
 * @returns The offered actions for this job's status.
 */
export function jobActionsFor(job: JobRowUI): Array<"retry" | "cancel" | "inspect" | "resume"> {
  switch (job.status) {
    case "failed": return ["retry", "inspect"];
    case "running": return ["cancel", "inspect"];
    case "queued": return ["cancel", "inspect"];
    case "cancelled": return ["retry", "inspect"];
    default: return ["inspect"];
  }
}

/** Current sync status for the sync-status banner. */
export interface SyncStatusUI { online: boolean; queueLength: number; lastSyncAt?: string; pendingConflicts: number; }
/**
 * Derive the sync banner's tone from current status.
 * @param s - Current sync status.
 * @returns `"danger"` if conflicts are pending, `"warn"` if offline with a queue, else `"ok"`.
 */
export function syncBannerTone(s: SyncStatusUI): "ok" | "warn" | "danger" {
  if (!s.online && s.queueLength > 0) return "warn";
  if (s.pendingConflicts > 0) return "danger";
  return "ok";
}

/** A storage usage/limit pair (MB). */
export interface StorageBudget { usedMB: number; limitMB: number; }
/**
 * Derive the storage budget's percentage, tone, and display message.
 * @param b - Current usage/limit.
 * @returns The percent used (0..100), a tone (`ok`/`warn`/`danger`), and a formatted message.
 */
export function storageBudgetState(b: StorageBudget): { pct: number; tone: "ok" | "warn" | "danger"; message: string } {
  const pct = b.limitMB <= 0 ? 0 : Math.min(100, Math.round((b.usedMB / b.limitMB) * 100));
  const tone = pct >= 95 ? "danger" : pct >= 80 ? "warn" : "ok";
  return { pct, tone, message: `${b.usedMB.toFixed(0)} / ${b.limitMB.toFixed(0)} MB` };
}

/** An AI provider row as shown in the admin AI-providers list. */
export interface AIProviderRowUI { providerId: string; label: string; status: "enabled" | "disabled" | "beta" | "deprecated"; credentialConfigured: boolean; }
/**
 * Whether a provider is usable right now.
 * @param p - The provider row to check.
 * @returns True if the provider is neither disabled nor deprecated and has a configured credential.
 */
export function aiProviderIsReady(p: AIProviderRowUI): boolean { return p.status !== "disabled" && p.status !== "deprecated" && p.credentialConfigured; }

/** Billing/plan summary for the Billing settings screen. */
export interface BillingSummaryUI { plan: string; renderCreditsRemaining: number; renderCreditsMonthly: number; nextResetAt?: string; }
/**
 * Derive the billing banner's tone from remaining vs. monthly render credits.
 * @param b - Current billing summary.
 * @returns `"danger"` at <=10% remaining, `"warn"` at <=30%, else `"ok"`.
 */
export function billingBannerTone(b: BillingSummaryUI): "ok" | "warn" | "danger" {
  if (b.renderCreditsMonthly <= 0) return "ok";
  const ratio = b.renderCreditsRemaining / b.renderCreditsMonthly;
  return ratio <= 0.1 ? "danger" : ratio <= 0.3 ? "warn" : "ok";
}

/**
 * Admin section visibility (per 68). Missing perms → hidden section, not disabled.
 * @param permissions - The user's permission strings.
 * @returns The admin sections this user may see.
 */
export function adminSectionsFor(permissions: string[]): Array<"moderation" | "support_audit" | "flag_accounts"> {
  const out: Array<"moderation" | "support_audit" | "flag_accounts"> = [];
  if (permissions.includes("support:moderate")) out.push("moderation");
  if (permissions.includes("support:read")) out.push("support_audit");
  if (permissions.includes("support:accounts:flag")) out.push("flag_accounts");
  return out;
}
